import React, { ComponentType, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import PageScrollerModal from './PageScrollerModal';

import { getListNameFromUrl } from 'crm/helpers/UrlHelper';

/**
 * Type definition for the component's props.
 * @property {string} listName - Passed name of the list the component is rendered in.
 * @property {ComponentType<any>} [ShowElement] - The component to show the element details.
 * @property {ComponentType<any>} [EditElement] - The component to edit the element.
 * @property {ComponentType<any>} [CreateElement] - The component to create a new element.
 * @property {Function} [update] - A function to call after closing the Modal.
 */
type Props = {
    listName: string;
    ShowElement?: ComponentType<any>;
    EditElement?: ComponentType<any>;
    CreateElement?: ComponentType<any>;
    update?: Function;
};

/**
 * A component that should be loaded in a list that renders a modal for showing, editing,
 * or creating an element based on the URL parameters.
 *
 * @param {Props} props
 * @returns {JSX.Element}
 */
function ListItemModalManager({ listName, ShowElement, EditElement, CreateElement, update }: Props) {
    const { search } = useLocation();
    const searchParams = new URLSearchParams(search);
    const showIdString = `${listName}_show`;
    const editIdString = `${listName}_edit`;
    const createString = `${listName}_create`;
    const showId = searchParams.get(showIdString);
    const editId = searchParams.get(editIdString);
    const create = searchParams.get(createString);
    const showModal = showId || editId || create;
    const history = useHistory();

    const closeModal = () => {
        searchParams.delete(showIdString);
        searchParams.delete(editIdString);
        searchParams.delete(createString);
        history.push({
            pathname: getListNameFromUrl(window.location.pathname),
            search: searchParams.toString(),
        });
        update();
    };

    useEffect(() => {
        if (!showId && !editId && !create) {
            update();
        }
    }, [showId, editId, create]);

    return (
        showModal && (
            <PageScrollerModal closeModal={closeModal}>
                <>
                    {showId && <ShowElement id={showId} />}
                    {editId && <EditElement id={editId} />}
                    {create && <CreateElement id={create} />}
                </>
            </PageScrollerModal>
        )
    );
}

export default ListItemModalManager;
